/*
 * ZEUS — logística.
 *
 * 1) Calculador de envío. Antes sólo sabía consultar un proxy de app
 *    (`/apps/...`); como esa app nunca se instaló, siempre caía al modo
 *    estimación y mostraba "Precio: se calcula al finalizar la compra" — o sea,
 *    un botón "Calcular" que no calculaba nada. Ahora, cuando no hay proxy, usa
 *    el endpoint nativo `/cart/shipping_rates.json`, que devuelve las tarifas
 *    reales configuradas en Shopify → Configuración → Envíos.
 *
 *    Ese endpoint cotiza sobre el carrito actual y exige provincia además del
 *    código postal. Con el carrito vacío devuelve una lista vacía: en ese caso
 *    mostramos los plazos y decimos con todas las letras que el costo se ve con
 *    productos en el carrito, en vez de fingir un precio.
 *
 * 2) Barra de envío gratis (sin cambios de comportamiento).
 */
(function () {
  'use strict';

  var quoteCache = new Map();
  var CACHE_TTL = 300 * 1000;
  var CACHE_LIMIT = 20;
  var REQUEST_TIMEOUT = 8000;

  function textElement(tag, className, value) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    element.textContent = value || '';
    return element;
  }

  function safeText(value, fallback, maxLength) {
    var text = String(value || fallback || '').trim();
    return text.slice(0, maxLength || 120);
  }

  function money(value) {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 2
    }).format(value);
  }

  function normalizeQuote(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var carrier = safeText(raw.carrier || raw.transportista, '', 60);
    var numericPrice = Number(raw.price);
    var currency = safeText(raw.currency, 'ARS', 3).toUpperCase();
    if (!carrier || currency !== 'ARS') return null;
    var invalidPrice =
      raw.price === null ||
      raw.price === undefined ||
      raw.price === '' ||
      !Number.isFinite(numericPrice) ||
      numericPrice < 0 ||
      numericPrice > 1e7;
    return {
      carrier: carrier,
      service: safeText(raw.service || raw.service_type || raw.servicio, 'Servicio estándar', 80),
      delivery: safeText(raw.delivery_type || raw.delivery || raw.entrega, 'A domicilio', 80),
      eta: safeText(raw.eta || raw.estimated_time || raw.plazo, '', 120),
      price: invalidPrice ? null : numericPrice,
      currency: 'ARS'
    };
  }

  function renderQuotes(container, quotes, estimated) {
    container.replaceChildren();
    quotes.forEach(function (quote) {
      var card = document.createElement('article');
      card.className = 'zs-shipping-quote';
      if (quote.price === 0) card.classList.add('zs-shipping-quote--free');
      card.appendChild(textElement('h4', 'zs-shipping-quote__carrier', quote.carrier));
      card.appendChild(
        textElement('span', 'zs-shipping-quote__badge', estimated ? 'Estimación' : 'Tarifa real')
      );

      var meta = document.createElement('p');
      meta.className = 'zs-shipping-quote__meta';
      meta.appendChild(textElement('span', '', quote.service + ' · ' + quote.delivery));
      if (quote.eta) meta.appendChild(textElement('span', '', quote.eta));

      var priceText;
      if (quote.price === null) {
        priceText = 'Precio: se calcula al finalizar la compra';
      } else if (quote.price === 0) {
        priceText = '¡Envío gratis!';
      } else {
        priceText = 'Precio: ' + money(quote.price);
      }
      meta.appendChild(textElement('span', 'zs-shipping-quote__price', priceText));

      card.appendChild(meta);
      container.appendChild(card);
    });
  }

  /* Plazos configurados por el negocio, sin precio. Se usan como respaldo y
     también para acompañar a las tarifas reales, que no traen ETA propia. */
  function fallbackQuotes(root) {
    var quotes = [];
    if (root.dataset.andreaniEnabled === 'true') {
      quotes.push({
        carrier: 'Andreani',
        service: 'Servicio según cobertura',
        delivery: 'Domicilio o sucursal',
        eta: 'Entrega estimada: ' + root.dataset.andreaniMin + ' a ' + root.dataset.andreaniMax + ' días hábiles',
        price: null,
        currency: 'ARS'
      });
    }
    if (root.dataset.correoEnabled === 'true') {
      quotes.push({
        carrier: 'Correo Argentino',
        service: 'Servicio según cobertura',
        delivery: 'Domicilio o sucursal',
        eta: 'Entrega estimada: ' + root.dataset.correoMin + ' a ' + root.dataset.correoMax + ' días hábiles',
        price: null,
        currency: 'ARS'
      });
    }
    if (root.dataset.pickupEnabled === 'true') {
      quotes.push({
        carrier: 'Retiro en local ZEUS',
        service: 'Sin costo de envío',
        delivery: 'Retiro en local',
        eta: root.dataset.pickupMessage || 'El local y el horario se confirman durante la compra',
        price: null,
        currency: 'ARS'
      });
    }
    return quotes;
  }

  function validPostalCode(value) {
    return /^\d{4}$/.test(value) || /^[A-Z]\d{4}[A-Z]{3}$/.test(value.toUpperCase());
  }

  function safeProxyUrl(endpoint) {
    try {
      var url = new URL(endpoint, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      if (!url.pathname.startsWith('/apps/')) return null;
      return url;
    } catch (error) {
      return null;
    }
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  function setBusy(form, busy) {
    var button = form.querySelector('[data-zs-shipping-submit]');
    form.setAttribute('aria-busy', String(busy));
    if (button) button.disabled = busy;
  }

  function cacheSet(key, quotes) {
    if (quoteCache.size >= CACHE_LIMIT) quoteCache.delete(quoteCache.keys().next().value);
    quoteCache.set(key, { quotes: quotes, expires: Date.now() + CACHE_TTL });
  }

  /* Convierte la respuesta de /cart/shipping_rates.json al formato de tarjeta.
     Los precios de ese endpoint vienen como string en unidades ("10000.00"). */
  function ratesToQuotes(rates, root) {
    var pickupEnabled = root.dataset.pickupEnabled === 'true';
    var quotes = rates.map(function (rate) {
      var days = Array.isArray(rate.delivery_days) ? rate.delivery_days.filter(function (d) {
        return Number.isFinite(Number(d));
      }) : [];
      var eta = '';
      if (days.length >= 2) {
        eta = 'Entrega estimada: ' + days[0] + ' a ' + days[days.length - 1] + ' días hábiles';
      } else if (days.length === 1) {
        eta = 'Entrega estimada: ' + days[0] + ' días hábiles';
      } else if (rate.delivery_range && rate.delivery_range.length) {
        eta = 'Entrega estimada entre ' + rate.delivery_range.join(' y ');
      }
      return {
        carrier: safeText(rate.presentment_name || rate.name, 'Envío', 60),
        service: safeText(rate.description, 'Servicio según cobertura', 80),
        delivery: 'A domicilio',
        eta: eta,
        price: Number(rate.price),
        currency: 'ARS'
      };
    });

    if (pickupEnabled) {
      quotes.push({
        carrier: 'Retiro en local ZEUS',
        service: 'Sin costo de envío',
        delivery: 'Retiro en local',
        eta: root.dataset.pickupMessage || 'El local y el horario se confirman durante la compra',
        price: 0,
        currency: 'ARS'
      });
    }
    return quotes;
  }

  function shopifyRatesUrl(postalCode, province) {
    var params = new URLSearchParams();
    params.set('shipping_address[zip]', postalCode);
    params.set('shipping_address[country]', 'Argentina');
    params.set('shipping_address[province]', province);
    return '/cart/shipping_rates.json?' + params.toString();
  }

  function initCalculator(root) {
    if (root.dataset.initialized === 'true' && typeof root._zsShippingSubmit === 'function') return;

    var form = root.querySelector('form');
    var button = root.querySelector('[data-zs-shipping-submit]');
    var input = root.querySelector('[data-zs-postal-code]');
    var province = root.querySelector('[data-zs-province]');
    var status = root.querySelector('[data-zs-shipping-status]');
    var results = root.querySelector('[data-zs-shipping-results]');
    var activeController;

    if (!form || !button || !input || !status || !results) return;
    root.dataset.initialized = 'true';

    function fail(message, reason) {
      status.dataset.state = 'error';
      status.textContent = message;
      emit('zs:shipping:error', { reason: reason });
    }

    function showEstimates(postalCode, note) {
      var estimates = fallbackQuotes(root);
      results.replaceChildren();
      if (!estimates.length) {
        fail('No hay transportistas activos para mostrar.', 'no_carriers');
        return;
      }
      renderQuotes(results, estimates, true);
      status.dataset.state = 'ready';
      status.textContent = note;
      emit('zs:shipping:quoted', { postalCode: postalCode, estimated: true, count: estimates.length });
    }

    /* Tarifas reales de Shopify. Es el camino por defecto: no necesita ninguna
       app instalada, sólo que haya tarifas cargadas en Configuración → Envíos. */
    function quoteWithShopify(postalCode, provinceName) {
      var cacheKey = ['shopify', postalCode, provinceName].join(':');
      var cached = quoteCache.get(cacheKey);
      if (cached && cached.expires > Date.now()) {
        renderQuotes(results, cached.quotes, false);
        status.dataset.state = 'ready';
        status.textContent = 'Envío a ' + postalCode + ', ' + provinceName + '.';
        emit('zs:shipping:quoted', { postalCode: postalCode, estimated: false, cached: true, count: cached.quotes.length });
        return;
      }

      if (activeController) activeController.abort();
      activeController = new AbortController();
      var timeout = window.setTimeout(function () { activeController.abort(); }, REQUEST_TIMEOUT);

      setBusy(form, true);
      status.dataset.state = 'loading';
      status.textContent = 'Consultando el costo de envío…';

      fetch(shopifyRatesUrl(postalCode, provinceName), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: activeController.signal
      })
        .then(function (response) {
          return response.json().then(function (payload) {
            return { status: response.status, payload: payload };
          });
        })
        .then(function (result) {
          if (result.status === 422) {
            var detail = result.payload && (result.payload.province || result.payload.zip);
            fail(
              Array.isArray(detail) && detail.length
                ? detail[0]
                : 'Revisá el código postal y la provincia.',
              'invalid_address'
            );
            results.replaceChildren();
            return;
          }
          if (result.status !== 200) throw new Error('quote_failed');

          var rates = (result.payload && result.payload.shipping_rates) || [];
          if (!rates.length) {
            /* Sin ítems en el carrito Shopify no puede cotizar. Pasa siempre en
               la ficha de producto antes de agregar nada. */
            var vacio = root.dataset.context === 'product'
              ? 'Agregá el producto al carrito para ver el costo exacto de envío a ' + postalCode + '. Mientras tanto, estos son los plazos:'
              : 'No hay envíos disponibles para ' + postalCode + ', ' + provinceName + '. Estos son los plazos de referencia:';
            showEstimates(postalCode, vacio);
            return;
          }

          var quotes = ratesToQuotes(rates, root);
          cacheSet(cacheKey, quotes);
          renderQuotes(results, quotes, false);
          status.dataset.state = 'ready';
          status.textContent = 'Envío a ' + postalCode + ', ' + provinceName + '.';
          emit('zs:shipping:quoted', { postalCode: postalCode, estimated: false, count: quotes.length });
        })
        .catch(function (error) {
          showEstimates(
            postalCode,
            error.name === 'AbortError'
              ? 'La consulta tardó demasiado. Mostramos los plazos sin precio.'
              : 'No pudimos obtener el costo ahora. Mostramos los plazos sin precio.'
          );
        })
        .finally(function () {
          window.clearTimeout(timeout);
          setBusy(form, false);
        });
    }

    /* Proxy de app externa. Tiene prioridad si el negocio lo configura. */
    function quoteWithProxy(url, postalCode) {
      var productArea = root.closest('.zs-prod-info');
      var variantInput = productArea ? productArea.querySelector('input[name="id"]') : null;
      var quantityInput = productArea ? productArea.querySelector('input[name="quantity"]') : null;
      var variantId = variantInput ? variantInput.value : root.dataset.variantId || '';
      var quantity = Math.max(1, Math.min(99, Number(quantityInput ? quantityInput.value : 1) || 1));
      var cacheKey = ['proxy', postalCode, variantId, quantity].join(':');
      var cached = quoteCache.get(cacheKey);

      if (cached && cached.expires > Date.now()) {
        renderQuotes(results, cached.quotes, false);
        status.dataset.state = 'ready';
        status.textContent = 'Cotización para ' + postalCode + '.';
        return;
      }

      if (activeController) activeController.abort();
      activeController = new AbortController();
      var timeout = window.setTimeout(function () { activeController.abort(); }, REQUEST_TIMEOUT);

      url.searchParams.set('postal_code', postalCode);
      url.searchParams.set('country', 'AR');
      url.searchParams.set('variant_id', variantId);
      url.searchParams.set('quantity', String(quantity));

      setBusy(form, true);
      status.dataset.state = 'loading';
      status.textContent = 'Consultando opciones de envío…';

      fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: activeController.signal
      })
        .then(function (response) {
          if (!response.ok) throw new Error('quote_failed');
          return response.json();
        })
        .then(function (payload) {
          var source = Array.isArray(payload) ? payload : payload && payload.quotes;
          var quotes = Array.isArray(source) ? source.slice(0, 12).map(normalizeQuote).filter(Boolean) : [];
          if (!quotes.length) throw new Error('empty_quotes');
          cacheSet(cacheKey, quotes);
          renderQuotes(results, quotes, false);
          status.dataset.state = 'ready';
          status.textContent = 'Cotización para ' + postalCode + '. La tarifa definitiva se confirma al finalizar la compra.';
          emit('zs:shipping:quoted', { postalCode: postalCode, estimated: false, count: quotes.length });
        })
        .catch(function (error) {
          showEstimates(
            postalCode,
            error.name === 'AbortError'
              ? 'La consulta tardó demasiado. Mostramos una estimación sin precio.'
              : 'No pudimos obtener tarifas. Mostramos una estimación sin precio.'
          );
        })
        .finally(function () {
          window.clearTimeout(timeout);
          setBusy(form, false);
        });
    }

    var handleSubmit = function (event) {
      event.preventDefault();
      var postalCode = input.value.replace(/\s+/g, '').toUpperCase();

      if (!validPostalCode(postalCode)) {
        fail('Ingresá un código postal argentino válido, por ejemplo 4000 o T4000ABC.', 'invalid_postal_code');
        input.focus();
        return;
      }

      var provinceName = province ? province.value.trim() : '';
      if (province && !provinceName) {
        fail('Elegí tu provincia para calcular el costo.', 'missing_province');
        province.focus();
        return;
      }

      var proxyUrl = root.dataset.live === 'true' ? safeProxyUrl(root.dataset.endpoint || '') : null;
      if (proxyUrl) {
        quoteWithProxy(proxyUrl, postalCode);
      } else if (provinceName) {
        quoteWithShopify(postalCode, provinceName);
      } else {
        showEstimates(postalCode, 'Estimación para ' + postalCode + '. El precio se confirma al finalizar la compra.');
      }
    };

    root._zsShippingSubmit = handleSubmit;
    form.addEventListener('submit', handleSubmit);
    button.addEventListener('click', handleSubmit);
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-zs-shipping-calculator]').forEach(initCalculator);
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('.zs-shipping-calculator__form');
    if (!form || event.defaultPrevented) return;
    var root = form.closest('[data-zs-shipping-calculator]');
    if (!root) return;
    initCalculator(root);
    if (!event.defaultPrevented && typeof root._zsShippingSubmit === 'function') {
      root._zsShippingSubmit(event);
    }
  });

  function updateFreeShipping(totalPrice) {
    document.querySelectorAll('[data-zs-free-shipping]').forEach(function (bar) {
      var threshold = Number(bar.dataset.thresholdCents);
      if (!Number.isFinite(threshold) || threshold <= 0) return;
      var remaining = threshold - totalPrice;
      var percentage = Math.max(0, Math.min(100, Math.floor((totalPrice * 100) / threshold)));
      var text = bar.querySelector('.zs-envio-gratis__texto');
      var progress = bar.querySelector('.zs-envio-gratis__barra');
      var fill = progress && progress.querySelector('span');
      if (text) {
        text.textContent =
          remaining > 0
            ? (bar.dataset.remainingText || 'Te faltan [falta] para obtener envío gratis.').replace(
                '[falta]',
                money(remaining / 100)
              )
            : bar.dataset.achievedText || '¡Tu compra tiene envío gratis!';
        text.classList.toggle('zs-envio-gratis__texto--ok', remaining <= 0);
      }
      if (progress) progress.setAttribute('aria-valuenow', String(percentage));
      if (fill) fill.style.width = percentage + '%';
    });
  }

  var cartRefreshTimer;
  function refreshFreeShipping(event) {
    var cart = event && event.detail && event.detail.cart;
    if (cart && Number.isFinite(Number(cart.total_price))) {
      updateFreeShipping(Number(cart.total_price));
      return;
    }
    window.clearTimeout(cartRefreshTimer);
    cartRefreshTimer = window.setTimeout(function () {
      fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('cart_failed');
          return response.json();
        })
        .then(function (payload) {
          updateFreeShipping(Number(payload.total_price) || 0);
        })
        .catch(function () {});
    }, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('zs:cart:rendered', function (event) {
    initAll(event.target || document);
    refreshFreeShipping(event);
  });
  document.addEventListener('zs:cart:updated', function (event) {
    initAll(document);
    refreshFreeShipping(event);
  });
})();
